import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Colors } from 'context';
import { CareerIcon } from 'atoms/CareerIcon/CareerIcon';
import { SkillIcon } from 'atoms/SkillIcon/SkillIcon';
import { careerType, careerLayout } from 'services/careerType';
import { desktop } from 'services/ui';
import { useMediaQuery } from 'services/hooks/useMediaQuery';
import { ICareerCourseRecommendation, ICareerSkillNode } from 'core/hooks/CareerSkillGraph';

import { useSkillDetail } from 'core/hooks/Growth/useSkillDetail';

import { ISkillGraphIndex } from '../model/skillGraphModel';
import { REQUIREMENT_LABEL_KEY, requirementDisplay } from '../model/skillLevelLabel';
import { passportCourses } from '../model/skillPassportCourses';
import {
  SKILL_STATUS_LOOK,
  STATUS_TINT,
  STATUS_TINT_DARK,
  STATUS_TINT_LIGHT,
  tintVars,
} from '../skillStatus';
import { SkillPassportAbout } from './passport/SkillPassportAbout';
import { SkillPassportActions } from './passport/SkillPassportActions';
import { SkillPassportCriteria } from './passport/SkillPassportCriteria';
import { SkillPassportMore } from './passport/SkillPassportMore';

/**
 * Паспорт навыка (ТЗ 0070, переработка ТЗ 0071 п. 6.4).
 *
 * 🔴 Больше НЕ модалка. Модалка 1040 px с маской закрывала карту целиком, то
 * есть паспорт навыка отнимал у человека ровно ту картину, ради которой навык
 * открывали: соседей, связи, место в профессии. ТЗ 0071:
 * — desktop (≥ 1220) — боковая панель 420 px справа, БЕЗ затемнения; холст
 *   сужается и остаётся кликабельным, другой узел выбирается прямо с карты;
 * — tablet и телефон — полноэкранная панель с явной кнопкой «Wróć do mapy».
 *
 * 🔴 Свой `<aside role="dialog">`, а не antd `Modal`/`Drawer`: оба рисуют маску
 * (прямо против ТЗ) и анимируют появление — а в фоновой вкладке анимации antd
 * не доигрывают, и исправная панель выглядит пустой.
 *
 * 🔴 `aria-modal` честный: на desktop панель НЕ модальна — карта рядом
 * доступна и мышью, и клавиатурой; полноэкранная — модальна, карты под ней не
 * видно.
 *
 * 🔴 Escape закрывает, и фокус ВОЗВРАЩАЕТСЯ на узел, с которого пришли
 * (`#skm-<id>` на холсте или `[data-node-id]` в списке). Без этого после
 * закрытия фокус падал на `<body>`, и клавиатурный человек начинал обход
 * страницы с шапки сайта.
 */
interface ICareerSkillPassport {
  index: ISkillGraphIndex;
  node: ICareerSkillNode | null;
  recommended: ICareerCourseRecommendation[];
  canOpenVacancies: boolean;
  vacanciesLocked: boolean;
  onOpenSkill: (slug: string) => void;
  /** Навык, с которого пришли по цепочке. `null` — возвращаться некуда. */
  backTo: { slug: string; name: string } | null;
  onBack: () => void;
  /** `false` — закрытие отменено (несохранённая оценка, человек передумал). */
  onClose: () => boolean | void;
  /** Высота панели на desktop — по высоте холста рядом. */
  height?: number;
  /** 0073: профессия карты — из неё «Zastosowanie w zawodzie». */
  trackCode?: string;
  /**
   * 0073: выбранный, но не сохранённый уровень. Передаёт карта, чтобы
   * спросить подтверждение при закрытии; без пропа паспорт держит его сам.
   */
  draftLevel?: number | null;
  onDraftChange?: (level: number | null) => void;
}

/**
 * Вернуть фокус на узел карты.
 *
 * Сначала список (`data-node-id`), потом холст (`skm-<id>`): в DOM одновременно
 * стоит только один вид, но порядок сделан явным, а не случайным. Перебор
 * вместо селектора с подстановкой — в идентификаторе узла есть двоеточие, а
 * `CSS.escape` в jsdom нет.
 */
export const focusSkillNode = (nodeId: string): boolean => {
  const inList = Array.from(document.querySelectorAll<HTMLElement>('[data-node-id]')).find(
    (element) => element.dataset.nodeId === nodeId,
  );
  const target = (inList || document.getElementById(`skm-${nodeId}`)) as
    | (HTMLElement & SVGElement)
    | null;
  if (!target || typeof target.focus !== 'function') return false;
  target.focus({ preventScroll: true });
  return true;
};

export const CareerSkillPassport = memo(
  ({
    index,
    node,
    recommended,
    canOpenVacancies,
    vacanciesLocked,
    onOpenSkill,
    backTo,
    onBack,
    onClose,
    height,
    trackCode = '',
    draftLevel: draftProp,
    onDraftChange,
  }: ICareerSkillPassport) => {
    const { t } = useTranslation();
    const [localDraft, setLocalDraft] = useState<number | null>(null);
    const draftLevel = draftProp !== undefined ? draftProp : localDraft;
    const setDraft = onDraftChange ?? setLocalDraft;
    const isDesktop = useMediaQuery(desktop.replace('@media ', ''));
    const titleRef = useRef<HTMLHeadingElement>(null);
    const look = node ? SKILL_STATUS_LOOK[node.status] : null;

    /**
     * 🔴 Запрос живёт в оболочке, а не внутри блока «подробнее»: `<details>`
     * не размонтирует содержимое, но колонка раньше жила под `destroyOnClose`
     * модалки, и каждый монтаж `useQuery` — это поход в сеть. Свежесть
     * проверяется сравнением слага: не показать профессии предыдущего навыка
     * под новым именем.
     */
    const { skill, loading: detailLoading } = useSkillDetail(node?.slug ?? '');
    const detail = skill && node && skill.slug === node.slug ? skill : null;

    const courses = useMemo(
      () => (node ? passportCourses(node, recommended) : { primary: null, alternatives: [] }),
      [node, recommended],
    );

    const nodeId = node?.id ?? null;

    /**
     * Закрыть и вернуть фокус на узел.
     *
     * ⚠️ Фокус переносится ПОСЛЕ закрытия и через `setTimeout`, а не сразу и не
     * через `requestAnimationFrame`: закрытие меняет адрес, карта
     * перерисовывается, и узел, получивший фокус до перерисовки, мог бы его
     * потерять. rAF в фоновой вкладке не вызывается вовсе.
     */
    const close = useCallback(() => {
      if (!nodeId) return;
      if (onClose() === false) return;
      window.setTimeout(() => focusSkillNode(nodeId), 0);
    }, [nodeId, onClose]);

    // Без управления сверху черновик принадлежит одному навыку.
    useEffect(() => {
      if (draftProp === undefined) setLocalDraft(null);
    }, [nodeId, draftProp]);

    useEffect(() => {
      if (!nodeId) return undefined;
      const onKey = (event: KeyboardEvent) => {
        // `defaultPrevented` — Escape уже забрал кто-то ближе к фокусу: меню
        // настроек или сам холст (он закрывает паспорт своим обработчиком).
        if (event.key !== 'Escape' || event.defaultPrevented) return;
        close();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [nodeId, close]);

    /**
     * Фокус на заголовок при открытии и при переходе по чипу: скринридер
     * называет новый навык, а Tab начинает обход паспорта с его начала.
     * `preventScroll` — иначе страница прыгала бы к панели.
     */
    useEffect(() => {
      if (!nodeId) return;
      titleRef.current?.focus({ preventScroll: true });
    }, [nodeId]);

    if (!node || !look) return null;

    const display = requirementDisplay(node);

    return (
      <Aside
        role="dialog"
        aria-modal={isDesktop ? 'false' : 'true'}
        aria-labelledby="skm-passport-title"
        $sheet={!isDesktop}
        $height={height}>
        <Head>
          {/* На полноэкранной панели карта не видна — выход назван словами и
              стоит первым, до имени навыка. */}
          {!isDesktop && (
            <BackToMap type="button" onClick={close}>
              <CareerIcon name="arrowRight" size={16} />
              {t('careerCenter.map.backToMap')}
            </BackToMap>
          )}
          <HeadRow>
            {/* `category` обязателен: без него навык без логотипа получает
                серую плашку, тогда как тот же навык на холсте — цветную. */}
            <SkillIcon
              slug={node.slug}
              iconUrl={node.iconUrl}
              name={node.name}
              category={node.category}
              size={40}
            />
            <HeadText>
              {/* 1. Название и статус. */}
              <Name id="skm-passport-title" ref={titleRef} tabIndex={-1}>
                {node.name}
              </Name>
              <Status $status={node.status} $accent={look.accent}>
                <CareerIcon name={look.icon} size={14} />
                {t(REQUIREMENT_LABEL_KEY[display])}
              </Status>
            </HeadText>
            {isDesktop && (
              <Close type="button" onClick={close} aria-label={t('careerCenter.map.close')}>
                ×
              </Close>
            )}
          </HeadRow>
          {/* 🔴 Возврат по цепочке НАЗЫВАЕТ навык: «назад» без имени
              заставляет вспоминать то самое, что забылось. */}
          {backTo && (
            <Back type="button" onClick={onBack}>
              <CareerIcon name="arrowRight" size={16} />
              {t('careerCenter.map.backToSkill', { name: backTo.name })}
            </Back>
          )}
        </Head>

        {/* ТЗ 0072, 3.3; 0073, 4.2 — что это и где я → что нужно уметь →
            два действия (оценить / научиться) → связи и справка (свёрнуто). */}
        <Body>
          <SkillPassportAbout
            node={node}
            mentionedInCv={detail ? detail.mentionedInCv : null}
            usageNote={detail?.professions.find((row) => row.code === trackCode)?.usageNote ?? ''}
          />
          <SkillPassportCriteria
            requiredLevel={node.requiredLevel}
            criteria={detail?.levelCriteria ?? []}
            loading={detailLoading && !detail}
          />
          {/* 0073: два действия вместо развёрнутой формы — «уже умею» и
              «хочу научиться»; учебный материал живёт во втором. */}
          <SkillPassportActions
            index={index}
            node={node}
            course={courses.primary}
            skillId={detail?.id ?? null}
            onOpenSkill={onOpenSkill}
            draftLevel={draftLevel}
            onDraftChange={setDraft}
          />
          <SkillPassportMore
            index={index}
            onOpenSkill={onOpenSkill}
            node={node}
            professions={detail?.professions ?? []}
            loading={detailLoading && !detail}
            canOpenVacancies={canOpenVacancies}
            vacanciesLocked={vacanciesLocked}
            alternatives={courses.alternatives}
          />
        </Body>
      </Aside>
    );
  },
);

/**
 * 🔴 Тинты статусов объявлены и здесь: плашка статуса читает переменную, а
 * объявления холста живут на его svg и сюда не наследуются. Значения
 * печатаются из тех же карт, руками не переписываются.
 */
const Aside = styled.aside<{ $sheet: boolean; $height?: number }>`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  ${tintVars(STATUS_TINT_LIGHT)}

  [data-theme='dark'] & {
    ${tintVars(STATUS_TINT_DARK)}
  }

  display: flex;
  flex-direction: column;
  background: ${Colors.white};
  color: ${Colors.black};
  overscroll-behavior: contain;

  ${({ $sheet, $height }) =>
    $sheet
      ? css`
          /* Полноэкранная панель: поверх шапки сайта и нижнего меню. */
          position: fixed;
          inset: 0;
          z-index: 1000;
          overflow: auto;
        `
      : css`
          /* Колонка рядом с холстом, той же высоты: скроллится сама панель,
             а не страница, — колесо над картой занято панорамой. */
          width: 420px;
          height: ${$height ? `${$height}px` : 'auto'};
          max-height: ${$height ? `${$height}px` : 'calc(100vh - 32px)'};
          position: sticky;
          top: 16px;
          overflow: auto;
          border: 1px solid ${Colors.system_gray};
          border-radius: ${careerLayout.cardRadius}px;
        `}
`;

/** Шапка прилипает: имя навыка и выход остаются на виду при длинном паспорте. */
const Head = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 16px 12px;
  background: ${Colors.white};
  border-bottom: 1px solid ${Colors.system_gray};
`;

const HeadRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const HeadText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const Name = styled.h2`
  ${careerType.h3()};
  color: ${Colors.black};
  margin: 0;
  overflow-wrap: anywhere;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;

/**
 * Статус — плашка с подложкой своего тона, а не цветной текст.
 *
 * 🔴 Подпись красится `Colors.black`, а НЕ акцентом: тинты посчитаны под
 * чёрный текст (14.5 : 1), акцент на них даёт в лучшем случае 4.8.
 */
const Status = styled.span<{ $status: ICareerSkillNode['status']; $accent: string }>`
  ${careerType.metaStrong()};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $status }) => STATUS_TINT[$status]};
  border: 1px solid ${({ $accent }) => $accent};
  color: ${Colors.black};
`;

/**
 * Стрелка развёрнута зеркально (`scaleX(-1)`) — отдельной фигуры «влево» в
 * наборе нет, а заводить её ради двух кнопок незачем.
 */
const backStyles = css`
  ${careerType.metaStrong()};
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;
  color: ${Colors.black};
  background: ${Colors.white};
  border: 1px solid ${Colors.system_gray};

  svg {
    transform: scaleX(-1);
  }
`;

const Back = styled.button`
  ${backStyles}
`;

const BackToMap = styled.button`
  ${backStyles}
`;

const Close = styled.button`
  flex: none;
  min-width: ${careerLayout.hitTarget}px;
  min-height: ${careerLayout.hitTarget}px;
  border: none;
  background: none;
  font-size: 24px;
  line-height: 1;
  color: ${Colors.gray_dark};
  cursor: pointer;
`;
